// Gedaechtnis fuer den Zustand einzelner Ansichten: Reiter, Filter, Suchtext,
// aufgeklappte Zeilen, Bildlaufposition.
//
// Warum es das braucht: App.tsx schaltet Ansichten ueber `view` um und
// DEMONTIERT die verlassene Ansicht. Wer aus dem Integritaetswaechter ein
// Projekt oeffnete und zurueckging, landete auf dem KPI-Reiter, Filter weg,
// Liste ganz oben — jedes Mal. Die Ansicht selbst kann sich nichts merken,
// weil ihr React-Zustand mit ihr stirbt. Also liegt das Gedaechtnis hier,
// ausserhalb des Komponentenbaums.
//
// Zwei Schichten:
//   * eine Map im Speicher — das ist die Wahrheit waehrend die Seite lebt;
//   * ein Spiegel im sessionStorage — damit auch F5 wieder auf demselben
//     Reiter mit denselben Filtern landet. Bewusst KEIN localStorage: die
//     Filter eines Arbeitstags sollen nicht Wochen spaeter noch kleben
//     (dieselbe Abwaegung wie in navigationPersistence).
//
// Pure Funktionen, Storage injizierbar: testbar ohne DOM. Werte muessen
// JSON-faehig sein; wer etwas anderes ablegt, verliert es beim Neuladen —
// nicht mehr, denn die Map haelt es trotzdem.

export const VIEW_MEMORY_KEY = 'osia_view_memory_v1';

export interface StorageLike {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
}

const memory = new Map<string, unknown>();
let hydrated = false;
let mirror: StorageLike | null = null;

const defaultStorage = (): StorageLike | null => {
  try {
    return typeof window !== 'undefined' && window.sessionStorage ? window.sessionStorage : null;
  } catch {
    // Zugriff auf sessionStorage kann werfen (Privatmodus, blockierte Site-Daten).
    return null;
  }
};

/** Einmalig den Spiegel einlesen — spaeter als beim ersten Zugriff geht nicht. */
const hydrate = (): void => {
  if (hydrated) return;
  hydrated = true;
  if (!mirror) mirror = defaultStorage();
  if (!mirror) return;
  try {
    const raw = mirror.getItem(VIEW_MEMORY_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return;
    for (const [k, v] of Object.entries(parsed)) {
      // Was im Speicher schon steht, ist juenger als der Spiegel.
      if (!memory.has(k)) memory.set(k, v);
    }
  } catch {
    // Kaputter Spiegel: ignorieren, die Seite laeuft ohne Gedaechtnis weiter.
  }
};

const persist = (): void => {
  if (!mirror) return;
  try {
    mirror.setItem(VIEW_MEMORY_KEY, JSON.stringify(Object.fromEntries(memory)));
  } catch {
    // Quota/Privatmodus — der Spiegel ist nice-to-have, nie ein Fehler.
  }
};

/** Wert ablegen. `undefined` loescht den Schluessel. */
export const remember = (key: string, value: unknown): void => {
  hydrate();
  if (value === undefined) memory.delete(key);
  else memory.set(key, value);
  persist();
};

/** Wert holen; undefined, wenn nichts gemerkt ist. */
export const recall = <T>(key: string): T | undefined => {
  hydrate();
  return memory.get(key) as T | undefined;
};

/**
 * Alles vergessen — beim Abmelden. Der naechste Nutzer am selben Tab soll
 * nicht mit den Filtern und der Bildlaufposition seines Vorgaengers starten.
 * Mit `prefix` nur einen Teilbaum (z. B. alles zu einer Ansicht).
 */
export const forget = (prefix?: string): void => {
  hydrate();
  if (prefix === undefined) memory.clear();
  else for (const k of Array.from(memory.keys())) if (k.startsWith(prefix)) memory.delete(k);
  persist();
};

/**
 * Nur fuer Tests: Speicher leeren und einen anderen Spiegel einsetzen.
 * `null` = kein Spiegel.
 */
export const __resetViewMemoryForTests = (storage: StorageLike | null): void => {
  memory.clear();
  hydrated = false;
  mirror = storage;
};
